/**
 * Circular GTA-style minimap drawn with Canvas 2D.
 * The map rotates with the camera yaw (up = camera forward); the player arrow sits at the centre.
 * Zoom: 1 css px = 0.9 m on foot, 1.6 m in a vehicle (smoothly interpolated).
 */
import { G } from './ctx';
import { TAU, damp } from './util';
import type { MapBlip } from './types';

const C = {
  land: '#3d444c',
  water: '#3a78c4',
  waterEdge: '#2c5e9c',
  building: '#2a2f36',
  roadEdge: '#5c636b',
  road: '#cbd0d6',
  cop: '#3f86e0',
  vehicle: 'rgba(255,255,255,.55)',
};

const BLIP_DEFAULT: Record<MapBlip['kind'], string> = {
  marker: '#d65cd6',
  objective: '#f7d354',
  target: '#e0393f',
  pickup: '#7fdc6a',
  car: '#3f86e0',
  hospital: '#ffffff',
  police: '#3f86e0',
  shop: '#4cc46a',
};

interface Pickup {
  x?: number;
  z?: number;
  pos?: { x: number; z: number };
  kind?: string;
  weapon?: string;
}

interface SegCache {
  src: unknown;
  /** packed [ax, az, bx, bz, w, mx, mz, reach] sorted by width */
  data: Float32Array;
  n: number;
}

export class Minimap {
  readonly el: HTMLCanvasElement;
  blips: MapBlip[] = [];
  private ctx: CanvasRenderingContext2D;
  private size = 200;
  private dpr = 1;
  private mPerPx = 0.9;
  private segs: SegCache | null = null;
  private pois: { x: number; z: number; kind: 'hospital' | 'police' }[] | null = null;
  private poisSrc: unknown = null;
  private font = '';
  private fontBold = '';

  constructor(size: number) {
    this.el = document.createElement('canvas');
    this.ctx = this.el.getContext('2d', { alpha: false }) as CanvasRenderingContext2D;
    this.resize(size);
  }

  resize(size: number): void {
    this.size = size;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    const S = Math.round(size * this.dpr);
    if (this.el.width !== S) this.el.width = S;
    if (this.el.height !== S) this.el.height = S;
    this.font = `${Math.round(9 * this.dpr)}px Bebas, Inter, sans-serif`;
    this.fontBold = `bold ${Math.round(8.5 * this.dpr)}px Inter, sans-serif`;
  }

  /** current css-px -> metres factor (for hints) */
  get metresPerPixel(): number {
    return this.mPerPx;
  }

  private buildSegs(): SegCache {
    const src = G.city.roadSegments;
    const idx = src.map((_, i) => i).sort((a, b) => src[a].width - src[b].width);
    const data = new Float32Array(src.length * 8);
    for (let k = 0; k < idx.length; k++) {
      const s = src[idx[k]];
      const o = k * 8;
      const hl = Math.hypot(s.bx - s.ax, s.bz - s.az) * 0.5;
      data[o] = s.ax;
      data[o + 1] = s.az;
      data[o + 2] = s.bx;
      data[o + 3] = s.bz;
      data[o + 4] = s.width;
      data[o + 5] = (s.ax + s.bx) * 0.5;
      data[o + 6] = (s.az + s.bz) * 0.5;
      data[o + 7] = hl + s.width;
    }
    return { src, data, n: src.length };
  }

  draw(dt: number): void {
    const p = G.player;
    const cam = G.cam;
    const city = G.city;
    if (!p || !cam || !city) return;
    if (!this.segs || this.segs.src !== city.roadSegments) this.segs = this.buildSegs();
    if (!this.pois || this.poisSrc !== city.pois) {
      this.poisSrc = city.pois;
      this.pois = [];
      for (const poi of city.pois) {
        if (poi.key === 'hospital' || poi.key === 'police') this.pois.push({ x: poi.pos.x, z: poi.pos.z, kind: poi.key });
      }
    }

    const target = p.vehicle ? 1.6 : 0.9;
    this.mPerPx = damp(this.mPerPx, target, 3.5, dt);
    const dpr = this.dpr;
    const S = this.el.width;
    const c = S * 0.5;
    const R = c - 1;
    const s = dpr / this.mPerPx; // device px per metre
    const px = p.pos.x;
    const pz = p.pos.z;
    const theta = cam.yaw + Math.PI;
    const cosT = Math.cos(theta);
    const sinT = Math.sin(theta);
    const Rm = R / s + 3; // visible radius in metres (+ margin)
    const ctx = this.ctx;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = C.land;
    ctx.fillRect(0, 0, S, S);
    ctx.save();
    ctx.beginPath();
    ctx.arc(c, c, R, 0, TAU);
    ctx.clip();

    // ---- world-space layer -----------------------------------------------------
    ctx.translate(c, c);
    ctx.rotate(theta);
    ctx.scale(s, s);
    ctx.translate(-px, -pz);

    const minX = px - Rm;
    const maxX = px + Rm;
    const minZ = pz - Rm;
    const maxZ = pz + Rm;

    // water
    const water = city.waterRects;
    ctx.fillStyle = C.water;
    for (let i = 0; i < water.length; i++) {
      const w = water[i];
      if (w.maxX < minX || w.minX > maxX || w.maxZ < minZ || w.minZ > maxZ) continue;
      ctx.fillRect(w.minX, w.minZ, w.maxX - w.minX, w.maxZ - w.minZ);
    }

    // buildings
    const b = city.buildingRects;
    ctx.fillStyle = C.building;
    for (let i = 0; i < b.length; i++) {
      const r = b[i];
      if (r.maxX < minX || r.minX > maxX || r.maxZ < minZ || r.minZ > maxZ) continue;
      ctx.fillRect(r.minX, r.minZ, r.maxX - r.minX, r.maxZ - r.minZ);
    }

    // roads: two passes (edge, fill), batched by width
    const seg = this.segs;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (let pass = 0; pass < 2; pass++) {
      ctx.strokeStyle = pass === 0 ? C.roadEdge : C.road;
      const extra = pass === 0 ? 1.6 : 0;
      let curW = -1;
      let open = false;
      const d = seg.data;
      for (let i = 0; i < seg.n; i++) {
        const o = i * 8;
        const reach = d[o + 7];
        const mx = d[o + 5];
        const mz = d[o + 6];
        if (mx + reach < minX || mx - reach > maxX || mz + reach < minZ || mz - reach > maxZ) continue;
        const w = d[o + 4];
        if (w !== curW) {
          if (open) ctx.stroke();
          ctx.beginPath();
          ctx.lineWidth = Math.max(w + extra, (pass === 0 ? 3 : 1.6) / s);
          curW = w;
          open = true;
        }
        ctx.moveTo(d[o], d[o + 1]);
        ctx.lineTo(d[o + 2], d[o + 3]);
      }
      if (open) ctx.stroke();
    }
    ctx.restore();

    // ---- screen-space layer (upright glyphs) -----------------------------------
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.save();
    ctx.beginPath();
    ctx.arc(c, c, R, 0, TAU);
    ctx.clip();

    const proj = (x: number, z: number, clampTo: number): [number, number, boolean] => {
      const dx = x - px;
      const dz = z - pz;
      let rx = (dx * cosT - dz * sinT) * s;
      let ry = (dx * sinT + dz * cosT) * s;
      const len = Math.hypot(rx, ry);
      let clamped = false;
      if (len > clampTo) {
        rx = (rx / len) * clampTo;
        ry = (ry / len) * clampTo;
        clamped = true;
      }
      return [c + rx, c + ry, clamped];
    };
    const rim = R - 9 * dpr;

    // nearby vehicles (tiny dots)
    const vehicles = G.entities.vehicles;
    ctx.fillStyle = C.vehicle;
    for (let i = 0; i < vehicles.length; i++) {
      const v = vehicles[i];
      if (v === p.vehicle || v.destroyed) continue;
      const dx = v.pos.x - px;
      const dz = v.pos.z - pz;
      if (dx * dx + dz * dz > Rm * Rm) continue;
      const [x, y] = proj(v.pos.x, v.pos.z, R);
      ctx.beginPath();
      ctx.arc(x, y, 1.7 * dpr, 0, TAU);
      ctx.fill();
    }

    // permanent POIs
    for (const poi of this.pois) {
      const [x, y] = proj(poi.x, poi.z, rim);
      this.glyph(ctx, poi.kind, x, y, BLIP_DEFAULT[poi.kind], undefined, dpr);
    }

    // weapon pickups (if the weapons module exposes them)
    const pickups = (G.weapons as unknown as { pickups?: Pickup[] }).pickups;
    if (Array.isArray(pickups)) {
      for (let i = 0; i < pickups.length; i++) {
        const pk = pickups[i];
        const x = pk.x ?? pk.pos?.x;
        const z = pk.z ?? pk.pos?.z;
        if (typeof x !== 'number' || typeof z !== 'number') continue;
        const dx = x - px;
        const dz = z - pz;
        if (dx * dx + dz * dz > Rm * Rm) continue;
        const kind = pk.kind ?? 'weapon';
        const col = kind === 'health' ? '#ff5a5a' : kind === 'armor' ? '#6cc4ff' : kind === 'cash' ? '#6fd25f' : '#ffffff';
        const [sx, sy] = proj(x, z, rim);
        this.dot(ctx, sx, sy, 3 * dpr, col, dpr);
      }
    }

    // mission blips
    for (let i = 0; i < this.blips.length; i++) {
      const bl = this.blips[i];
      const [x, y, clamped] = proj(bl.x, bl.z, rim);
      this.glyph(ctx, bl.kind, x, y, bl.color || BLIP_DEFAULT[bl.kind], bl.label, dpr, clamped);
    }

    // cops
    const cops = G.police.cops;
    for (let i = 0; i < cops.length; i++) {
      const cp = cops[i];
      if (cp.state === 'dead') continue;
      const [x, y] = proj(cp.pos.x, cp.pos.z, rim);
      this.dot(ctx, x, y, 3 * dpr, C.cop, dpr);
    }
    const copCars = G.police.copCars;
    for (let i = 0; i < copCars.length; i++) {
      const cv = copCars[i];
      if (cv.destroyed || cv === p.vehicle) continue;
      const [x, y] = proj(cv.pos.x, cv.pos.z, rim);
      this.dot(ctx, x, y, 4 * dpr, C.cop, dpr);
    }

    // player arrow
    ctx.save();
    ctx.translate(c, c);
    ctx.rotate(cam.yaw - p.heading);
    const a = (p.vehicle ? 8 : 7) * dpr;
    ctx.beginPath();
    ctx.moveTo(0, -a);
    ctx.lineTo(a * 0.75, a * 0.8);
    ctx.lineTo(0, a * 0.35);
    ctx.lineTo(-a * 0.75, a * 0.8);
    ctx.closePath();
    ctx.fillStyle = '#fff';
    ctx.strokeStyle = 'rgba(0,0,0,.85)';
    ctx.lineWidth = 1.5 * dpr;
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.fill();
    ctx.restore();

    // inner rim shading + north marker
    ctx.beginPath();
    ctx.arc(c, c, R - 2 * dpr, 0, TAU);
    ctx.strokeStyle = 'rgba(0,0,0,.35)';
    ctx.lineWidth = 5 * dpr;
    ctx.stroke();
    const nx = c + sinT * (R - 8 * dpr);
    const ny = c - cosT * (R - 8 * dpr);
    ctx.beginPath();
    ctx.arc(nx, ny, 6 * dpr, 0, TAU);
    ctx.fillStyle = '#12161c';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,.8)';
    ctx.lineWidth = 1 * dpr;
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.font = this.font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('N', nx, ny + 0.5 * dpr);
    ctx.restore();
  }

  private dot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string, dpr: number): void {
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 1 * dpr;
    ctx.strokeStyle = 'rgba(0,0,0,.7)';
    ctx.stroke();
  }

  private glyph(ctx: CanvasRenderingContext2D, kind: MapBlip['kind'], x: number, y: number, color: string, label: string | undefined, dpr: number, clamped = false): void {
    const r = (kind === 'pickup' ? 3.5 : 7) * dpr;
    ctx.lineWidth = 1.5 * dpr;
    switch (kind) {
      case 'pickup':
        this.dot(ctx, x, y, r, color, dpr);
        return;
      case 'car': {
        // small car silhouette (rounded body + cabin)
        ctx.fillStyle = color;
        ctx.strokeStyle = 'rgba(0,0,0,.75)';
        const w = 12 * dpr;
        const h = 7 * dpr;
        ctx.beginPath();
        ctx.roundRect(x - w / 2, y - h / 2, w, h, 2 * dpr);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = 'rgba(255,255,255,.85)';
        ctx.fillRect(x - w * 0.22, y - h * 0.3, w * 0.44, h * 0.6);
        return;
      }
      case 'hospital': {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, TAU);
        ctx.fillStyle = '#1e2329';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,.9)';
        ctx.stroke();
        ctx.fillStyle = color;
        ctx.fillRect(x - 1.4 * dpr, y - 4.2 * dpr, 2.8 * dpr, 8.4 * dpr);
        ctx.fillRect(x - 4.2 * dpr, y - 1.4 * dpr, 8.4 * dpr, 2.8 * dpr);
        return;
      }
      case 'police': {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, TAU);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,.9)';
        ctx.stroke();
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        for (let i = 0; i < 10; i++) {
          const rr = (i % 2 === 0 ? 4.4 : 1.9) * dpr;
          const ang = -Math.PI / 2 + (i * Math.PI) / 5;
          const vx = x + Math.cos(ang) * rr;
          const vy = y + Math.sin(ang) * rr;
          if (i === 0) ctx.moveTo(vx, vy);
          else ctx.lineTo(vx, vy);
        }
        ctx.closePath();
        ctx.fill();
        return;
      }
      case 'target': {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, TAU);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,.9)';
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x, y, 2 * dpr, 0, TAU);
        ctx.fillStyle = '#fff';
        ctx.fill();
        break;
      }
      case 'objective':
      case 'marker':
      case 'shop':
      default: {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, TAU);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,.92)';
        ctx.stroke();
        if (kind === 'objective' && !label) {
          ctx.beginPath();
          ctx.arc(x, y, 2.2 * dpr, 0, TAU);
          ctx.fillStyle = 'rgba(0,0,0,.55)';
          ctx.fill();
        }
        break;
      }
    }
    const txt = label ?? (kind === 'shop' ? '$' : undefined);
    if (txt) {
      ctx.fillStyle = kind === 'objective' ? '#1b1b1b' : '#fff';
      ctx.font = this.fontBold;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(txt.slice(0, 2), x, y + 0.5 * dpr);
    }
    if (clamped) {
      // small pointer chevron toward the off-screen target
      const cx = this.el.width * 0.5;
      const ang = Math.atan2(y - cx, x - cx);
      ctx.save();
      ctx.translate(x + Math.cos(ang) * (r + 2 * dpr), y + Math.sin(ang) * (r + 2 * dpr));
      ctx.rotate(ang);
      ctx.beginPath();
      ctx.moveTo(0, -3.5 * dpr);
      ctx.lineTo(3.5 * dpr, 0);
      ctx.lineTo(0, 3.5 * dpr);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
      ctx.restore();
    }
  }
}
